import React from 'react';
import Icon from '@atlaskit/icon';
import DiscoverIcon from '@atlaskit/icon/glyph/discover';
import EditorAddIcon from '@atlaskit/icon/glyph/editor/add';
import EmojiFrequentIcon from '@atlaskit/icon/glyph/emoji/frequent';
import { MenuGroup, LinkItem } from '@atlaskit/menu';
import { AutomationGlyph } from '@atlassian/jira-automation-menu/src/common/assets/automation';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { ProjectProps } from '../../../common/types';
import {
	buildDiscoverAutomationUrl,
	buildAuditLogAutomationUrl,
	buildManageRulesAutomationUrl,
	buildNewRuleAutomationUrl,
} from '../../../common/utils/automation-urls';
import messages from './messages';

const automationIcon = <Icon glyph={AutomationGlyph} label="" />;

type Props = ProjectProps & {
	includeDiscoverAutomation: boolean;
};

export const AutomationMenuDeepLinks = ({
	isSimplifiedProject,
	projectKey,
	projectType,
	includeDiscoverAutomation,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'link',
	});
	const { formatMessage } = useIntl();
	return (
		// eslint-disable-next-line jira/integration/test-id-by-folder-structure
		<div data-testid="jira-releases-automation-menu.ui.automation-menu-content">
			<MenuGroup>
				{includeDiscoverAutomation ? (
					<LinkItem
						href={buildDiscoverAutomationUrl({
							projectKey,
							projectType,
							isSimplified: isSimplifiedProject,
						})}
						iconBefore={<DiscoverIcon label="" />}
						onClick={() => fireUIAnalytics(analyticsEvent, 'discoverAutomationLink')}
						UNSAFE_shouldDisableRouterLink
					>
						{formatMessage(messages.discoverAutomationMenuItemLabel)}
					</LinkItem>
				) : null}
				<LinkItem
					href={buildManageRulesAutomationUrl({
						projectKey,
						projectType,
						isSimplified: isSimplifiedProject,
					})}
					iconBefore={automationIcon}
					onClick={() => fireUIAnalytics(analyticsEvent, 'manageRulesAutomationLink')}
					UNSAFE_shouldDisableRouterLink
				>
					{formatMessage(messages.manageAutomationMenuItemLabel)}
				</LinkItem>
				<LinkItem
					href={buildNewRuleAutomationUrl({
						projectKey,
						projectType,
						isSimplified: isSimplifiedProject,
					})}
					iconBefore={<EditorAddIcon label="" />}
					onClick={() => fireUIAnalytics(analyticsEvent, 'newRuleAutomationLink')}
					UNSAFE_shouldDisableRouterLink
				>
					{formatMessage(messages.createAutomationMenuItemLabel)}
				</LinkItem>
				<LinkItem
					href={buildAuditLogAutomationUrl({
						projectKey,
						projectType,
						isSimplified: isSimplifiedProject,
					})}
					iconBefore={<EmojiFrequentIcon label="" />}
					onClick={() => fireUIAnalytics(analyticsEvent, 'auditLogAutomationLink')}
					UNSAFE_shouldDisableRouterLink
				>
					{formatMessage(messages.auditLogAutomationMenuItemLabel)}
				</LinkItem>
			</MenuGroup>
		</div>
	);
};
